import React from "react";
import { Button } from "@/ui-components/button-with-loading";
import { useTagSearch, type Tag } from "@/shared/api/newsquawk-headlines";
import SelectableBadge from "@/ui-components/badge";

interface AdditionalTagSearchProps {
  /** Callback when a tag is toggled (selected/deselected) */
  onTagToggle: (tag: Tag) => void;
  /** Function to check if a tag is currently selected */
  isTagSelected: (tagId: number) => boolean;
  /** Callback to select all search result tags */
  onSelectAll: (tags: Tag[]) => void;
}

function AdditionalTagSearch({
  onTagToggle,
  isTagSelected,
  onSelectAll,
}: AdditionalTagSearchProps) {
  const [query, setQuery] = React.useState("");
  const [searchedQuery, setSearchedQuery] = React.useState("");

  const { search, tags, isLoading, isSuccess, clearSearch } = useTagSearch();

  const handleSubmit = () => {
    if (query.length === 0) {
      setSearchedQuery("");
      clearSearch();
      return;
    }

    setSearchedQuery(query);
    search(query);
  };

  // Only show results if they match the current query
  const shouldShowResults = query === searchedQuery && query.length > 0;

  const allSelected =
    tags.length > 0 && tags.every((tag) => isTagSelected(tag.tag_id));

  return (
    <div className="col-span-2">
      <span className="text-sm/8 font-medium text-gray-300 flex items-center gap-2">
        Additional Tags
      </span>
      <div className="space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="block min-w-0 w-full grow p-1 pr-4 text-sm text-gray-900  focus:outline-none sm:text-sm/6 w-100 rounded border-none"
            placeholder="Search for tags"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <Button
            className="min-w-fit text-red border-2 border-squawk-blue-light"
            variant="secondary"
            onClick={handleSubmit}
            disabled={query.length === 0 || isLoading}
            loading={isLoading}
          >
            Search Tags
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {shouldShowResults && isSuccess && tags.length === 0 && (
            <div className="text-sm text-gray-400 py-2">
              No tags found for &quot;{searchedQuery}&quot;. Try a different
              search term.
            </div>
          )}

          {shouldShowResults && tags.length > 1 && (
            <SelectableBadge
              key={0}
              text="SelectAll"
              selected={allSelected}
              onSelect={() => {
                if (allSelected) {
                  // Deselect all
                  tags.forEach((tag) => {
                    if (isTagSelected(tag.tag_id)) {
                      onTagToggle(tag);
                    }
                  });
                } else {
                  // Select all that aren't selected
                  onSelectAll(tags);
                }
              }}
            />
          )}

          {shouldShowResults &&
            tags.map((tag) => (
              <SelectableBadge
                key={tag.tag_id}
                text={tag.name}
                selected={isTagSelected(tag.tag_id)}
                onSelect={() => onTagToggle(tag)}
              />
            ))}
        </div>
      </div>
    </div>
  );
}

export default AdditionalTagSearch;
